import { useCallback, useEffect, useState } from "react";
import type { SessionUser } from "@rma-orbit/shared";
import { ApiError, api, setSessionLostHandler } from "./api/client.js";
import { Login } from "./pages/Login.js";
import { Dashboard } from "./pages/Dashboard.js";
import { Collect } from "./pages/Collect.js";
import { Console } from "./pages/Console.js";
import { Firewall } from "./pages/Firewall.js";
import { KnowledgeMap } from "./pages/KnowledgeMap.js";
import { Cases } from "./pages/Cases.js";
import { Intake } from "./pages/Intake.js";
import { Calendar } from "./pages/Calendar.js";
import { Shipping } from "./pages/Shipping.js";
import { Agent } from "./pages/Agent.js";
import { CaseDetail } from "./pages/CaseDetail.js";
import { Devices } from "./pages/Devices.js";
import { Companies } from "./pages/Companies.js";
import { ImportExcel } from "./pages/ImportExcel.js";
import { Users } from "./pages/Users.js";
import { ApiKeys } from "./pages/ApiKeys.js";
import { System } from "./pages/System.js";
import { Busy } from "./lib/ui.js";

/**
 * 화면 골격 (harness/UI-SPEC.md §1). 라우팅은 해시로 한다 — 정적 자산을 Workers 가
 * 그대로 서빙하므로 SPA fallback 설정이 필요 없고, 새로고침해도 같은 화면이 뜬다.
 *
 * 관리 화면은 하나의 설정 탭에 몰지 않는다 — 협력사·엑셀 이관·계정·API·시스템이 각자
 * 화면이다 (2026-09-09 대표 지시). `main` 은 경로마다 다시 마운트되어 진입 애니메이션이 돈다.
 */

export type Route =
  | { name: "dashboard" }
  | { name: "cases"; swap?: "any" | "none" }
  | { name: "intake" }
  | { name: "calendar" }
  | { name: "shipping" }
  | { name: "agent" }
  | { name: "case"; id: number }
  | { name: "devices"; serial?: string }
  | { name: "collect" }
  | { name: "console" }
  | { name: "firewall" }
  | { name: "map" }
  | { name: "companies" }
  | { name: "import" }
  | { name: "users" }
  | { name: "apikeys" }
  | { name: "system" };

function parseHash(): Route {
  // 해시에도 조건이 붙는다 (`#/cases?swap=none`). 떼어내지 않으면 head 가
  // "cases?swap=none" 이 되어 아무 경로에도 안 맞고 조용히 대시보드로 떨어진다.
  const full = window.location.hash.replace(/^#\/?/, "");
  const queryAt = full.indexOf("?");
  const raw = queryAt >= 0 ? full.slice(0, queryAt) : full;
  const params = new URLSearchParams(queryAt >= 0 ? full.slice(queryAt + 1) : "");

  const [head, tail] = raw.split("/");
  if (head === "cases" && tail) return { name: "case", id: Number(tail) };
  if (head === "cases") {
    const swap = params.get("swap");
    return { name: "cases", swap: swap === "any" || swap === "none" ? swap : undefined };
  }
  if (head === "intake") return { name: "intake" };
  if (head === "calendar") return { name: "calendar" };
  if (head === "shipping") return { name: "shipping" };
  if (head === "agent") return { name: "agent" };
  if (head === "devices") return { name: "devices", serial: tail ? decodeURIComponent(tail) : undefined };
  if (head === "collect") return { name: "collect" };
  if (head === "console") return { name: "console" };
  if (head === "firewall") return { name: "firewall" };
  if (head === "map") return { name: "map" };
  if (head === "companies") return { name: "companies" };
  if (head === "import") return { name: "import" };
  if (head === "users") return { name: "users" };
  if (head === "apikeys") return { name: "apikeys" };
  // 예전 링크(#/settings)는 시스템으로 — 설정 탭은 다섯 화면으로 나뉘었다
  if (head === "system" || head === "settings") return { name: "system" };
  return { name: "dashboard" };
}

export function navigate(hash: string): void {
  window.location.hash = hash;
}

export interface NavItem {
  hash: string;
  label: string;
  icon: string;
  match: Route["name"][];
  /** 이 권한이 없으면 메뉴에서 뺀다 — 못 여는 화면으로 사람을 보내지 않는다 */
  need?: string;
}

export const NAV: Array<{ group: string; items: NavItem[] }> = [
  {
    group: "업무",
    items: [
      { hash: "#/", label: "대시보드", icon: "◫", match: ["dashboard"] },
      { hash: "#/cases", label: "케이스 목록", icon: "☰", match: ["cases", "case"] },
      { hash: "#/intake", label: "입고 접수", icon: "⇩", match: ["intake"], need: "case.write" },
      { hash: "#/devices", label: "장비(시리얼)", icon: "▣", match: ["devices"] },
      { hash: "#/calendar", label: "달력", icon: "▦", match: ["calendar"] },
    ],
  },
  {
    group: "장비 작업",
    items: [
      { hash: "#/console", label: "콘솔·증적", icon: ">_", match: ["console"] },
      { hash: "#/firewall", label: "방화벽 셋팅", icon: "⚙", match: ["firewall"] },
      { hash: "#/shipping", label: "택배 송장", icon: "✈", match: ["shipping"] },
      { hash: "#/collect", label: "SECUI 수집", icon: "⇅", match: ["collect"], need: "admin.users" },
    ],
  },
  {
    group: "데이터",
    items: [
      { hash: "#/agent", label: "에이전트에게 묻기", icon: "✦", match: ["agent"] },
      { hash: "#/companies", label: "협력사", icon: "◎", match: ["companies"] },
      { hash: "#/import", label: "엑셀 이관", icon: "⇪", match: ["import"], need: "case.write" },
      { hash: "#/map", label: "지식 맵", icon: "⌘", match: ["map"] },
    ],
  },
  {
    group: "관리",
    items: [
      { hash: "#/users", label: "계정·권한", icon: "♟", match: ["users"], need: "admin.users" },
      { hash: "#/apikeys", label: "API Key · MCP", icon: "⚿", match: ["apikeys"], need: "admin.users" },
      { hash: "#/system", label: "시스템", icon: "≡", match: ["system"], need: "admin.users" },
    ],
  },
];

const CRAWL_POLL_MS = 60_000;

export function App() {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [checking, setChecking] = useState(true);
  const [route, setRoute] = useState<Route>(parseHash);
  const [sessionLost, setSessionLost] = useState(false);
  // 좁은 화면(폰)에서는 사이드바가 서랍이 된다 — 열림 상태 (styles.css `@media (max-width: 760px)`)
  const [navOpen, setNavOpen] = useState(false);
  const [crawlAlert, setCrawlAlert] = useState(false);
  // 로그인 직후 한 번만 앱 골격이 미끄러져 들어온다
  const [entering, setEntering] = useState(false);

  useEffect(() => {
    const onHash = () => setRoute(parseHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  useEffect(() => {
    // 세션이 끝나 401 이 오면 어느 화면에 있든 로그인으로 돌아간다 (UI-SPEC §4-1)
    setSessionLostHandler(() => {
      // 로그인한 적이 있을 때만 "세션이 끝났다" 고 말한다. 처음 연 사람의 /me 401 은
      // 끊긴 것이 아니라 아직 안 들어온 것이다 — 그 사람에게 세션 만료 안내가 떴었다
      setUser((prev) => {
        if (prev !== null) setSessionLost(true);
        return null;
      });
    });
  }, []);

  useEffect(() => {
    api
      .me()
      .then(setUser)
      .catch((err) => {
        // 401 은 "아직 로그인하지 않았다" 일 뿐이다 — 오류로 보여주지 않는다
        if (!(err instanceof ApiError) || err.status !== 401) console.error(err);
        setUser(null);
      })
      .finally(() => setChecking(false));
  }, []);

  const logout = useCallback(async () => {
    await api.logout().catch(() => undefined);
    setUser(null);
    navigate("#/");
  }, []);

  useEffect(() => {
    // 권한이 없으면 부르지 않는다 — 부를 수 없는 API 를 부르고 401 을 삼키지 않는다
    if (user === null || !user.permissions.includes("admin.users")) {
      setCrawlAlert(false);
      return;
    }
    let alive = true;
    const load = () => {
      api
        .crawlStatus()
        .then((s) => {
          if (alive) setCrawlAlert(s.needs_login || s.stale);
        })
        .catch(() => {
          // 상태를 못 읽은 것과 "수집이 문제" 인 것은 다르다 — 조용히 둔다
        });
    };
    load();
    const timer = window.setInterval(load, CRAWL_POLL_MS);
    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, [user]);

  if (checking) {
    return (
      <div className="login-wrap">
        <Busy>세션 확인 중…</Busy>
      </div>
    );
  }

  if (!user) {
    return (
      <Login
        expired={sessionLost}
        onLoggedIn={(u) => {
          setSessionLost(false);
          setEntering(true);
          setUser(u);
          window.setTimeout(() => setEntering(false), 900);
        }}
      />
    );
  }

  const can = (permission: string) => user.permissions.includes(permission as never);
  // 경로가 바뀌면 main 이 다시 마운트되어 카드들이 차례로 떠오른다 (styles.css `main > *`)
  const routeKey = `${route.name}:${"id" in route ? route.id : ""}${"serial" in route ? (route.serial ?? "") : ""}`;

  return (
    <div className={entering ? "app-enter" : undefined}>
      <header className="topbar">
        <button
          className={`menu-btn ${navOpen ? "on" : ""}`}
          aria-label="메뉴"
          aria-expanded={navOpen}
          onClick={() => setNavOpen((v) => !v)}
        >
          <span />
          <span />
          <span />
        </button>
        <div className="brand">
          <span className="brand-mark" aria-hidden="true" />
          RMA <span>ORBIT</span>
          <span className="brand-sub">Triz MS</span>
        </div>
        <div className="spacer" />
        <div className="who">
          {user.name} · {user.email}
        </div>
        <button className="ghost" onClick={logout}>
          로그아웃
        </button>
      </header>

      {navOpen && <div className="nav-backdrop" onClick={() => setNavOpen(false)} />}
      <nav className={`sidebar ${navOpen ? "open" : ""}`}>
        {NAV.map((group) => {
          const items = group.items.filter((item) => !item.need || can(item.need));
          if (items.length === 0) return null;
          return (
            <div className="group" key={group.group}>
              <div className="group-title">{group.group}</div>
              {items.map((item) => (
                <button
                  key={item.hash}
                  className={`navitem ${item.match.includes(route.name) ? "active" : ""}`}
                  onClick={() => {
                    setNavOpen(false);
                    navigate(item.hash);
                  }}
                  title={item.label}
                >
                  <span className="navicon" aria-hidden="true">{item.icon}</span>
                  {item.label}
                  {crawlAlert && item.hash === "#/collect" && (
                    <span className="navbadge" title="SECUI 수집이 멈췄습니다 — 눌러서 다시 로그인해 주세요">
                      !
                    </span>
                  )}
                </button>
              ))}
            </div>
          );
        })}
      </nav>

      <main key={routeKey}>
        {route.name === "dashboard" && <Dashboard can={can} user={user} />}
        {route.name === "cases" && <Cases can={can} initialSwap={route.swap} />}
        {route.name === "intake" && <Intake />}
        {route.name === "calendar" && <Calendar can={can} />}
        {route.name === "shipping" && <Shipping can={can} />}
        {route.name === "agent" && <Agent />}
        {route.name === "case" && <CaseDetail id={route.id} can={can} />}
        {route.name === "devices" && <Devices serial={route.serial} can={can} />}
        {route.name === "collect" && <Collect can={can} />}
        {route.name === "console" && <Console can={can} />}
        {route.name === "firewall" && <Firewall />}
        {route.name === "map" && <KnowledgeMap />}
        {route.name === "companies" && <Companies can={can} />}
        {route.name === "import" && <ImportExcel />}
        {route.name === "users" && <Users />}
        {route.name === "apikeys" && <ApiKeys />}
        {route.name === "system" && <System />}
      </main>
    </div>
  );
}
